import { useState } from "react";
import { ChevronDown, ChevronUp, Loader2, MonitorCheck, Printer, Zap } from "lucide-react";
import { printBadge, silentPrintStatus, isChromium, SAMPLE_VISIT } from "@/lib/visitorBadge";

const APP_URL = `${window.location.origin}/kiosk`;
const STEPS = [
  {
    os: "Windows",
    steps: [
      "Right-click the Chrome (or Edge) shortcut on the kiosk PC → Properties.",
      { text: 'In "Target", add a space and then:', code: `--kiosk-printing --kiosk "${APP_URL}"` },
      "Set the label printer as the Windows default printer, with the label size as its default paper.",
      "Launch Chrome from that shortcut. Badges now print with no dialog.",
    ],
  },
  {
    os: "macOS",
    steps: [
      { text: "Open Terminal and run:", code: `open -na "Google Chrome" --args --kiosk-printing --kiosk "${APP_URL}"` },
      "Set the label printer as the default in System Settings → Printers, with the label as its default paper size.",
      "Tip: save the command as an Automator/Shortcuts app so staff can double-click it.",
    ],
  },
  {
    os: "Chromebook / ChromeOS",
    steps: [
      "ChromeOS doesn't accept launch flags. Ask your Google Workspace admin to set the Chrome policy \"PrintPreviewUseSystemDefaultPrinter\" and deploy Logerd as a kiosk app.",
      "Alternatively use a Windows/macOS mini-PC or an Android tablet with Chrome for the kiosk.",
    ],
  },
];

export function SilentPrintGuide({ cfg }) {
  const [open, setOpen] = useState(false);
  const [testing, setTesting] = useState(false);
  const [status, setStatus] = useState(silentPrintStatus());
  const [tab, setTab] = useState(0);

  const test = async () => {
    setTesting(true);
    const { silent } = await printBadge(SAMPLE_VISIT, cfg);
    setStatus(silent === null ? status : silent ? "1" : "0");
    setTesting(false);
  };

  const chip = status === "1"
    ? { cls: "bg-emerald-100 text-emerald-800", text: "This device prints silently" }
    : status === "0"
      ? { cls: "bg-amber-100 text-amber-800", text: "This device shows the print dialog" }
      : { cls: "bg-slate-100 text-slate-600", text: "Not tested on this device yet" };

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50" data-testid="silent-print-guide">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="silent-print-toggle" className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left">
        <span className="flex items-center gap-2 text-sm font-semibold text-slate-900"><Zap className="h-4 w-4 text-primary" /> Zero-tap printing (skip the print dialog)</span>
        <span className="flex items-center gap-3">
          <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${chip.cls}`} data-testid="silent-print-status">{chip.text}</span>
          {open ? <ChevronUp className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
        </span>
      </button>
      {open && (
        <div className="border-t border-slate-200 px-4 py-4 space-y-4">
          <p className="text-xs text-slate-600">
            Browsers always show a print dialog — except Chrome or Edge started with the <code className="rounded bg-white px-1 py-0.5 font-mono text-[11px] ring-1 ring-slate-200">--kiosk-printing</code> flag,
            which sends every print straight to the default printer. Set that up once on the kiosk device, turn on “Print automatically after check-in” above, and badges come out with zero taps.
          </p>
          <div className="flex rounded-lg border border-slate-200 bg-white p-1 gap-1 w-fit">
            {STEPS.map((s, i) => (
              <button key={s.os} type="button" onClick={() => setTab(i)} data-testid={`silent-print-os-${i}`}
                      className={`h-8 rounded-md px-3 text-xs font-semibold transition ${tab === i ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{s.os}</button>
            ))}
          </div>
          <ol className="space-y-1.5 text-sm text-slate-700 list-decimal pl-5" data-testid="silent-print-steps">
            {STEPS[tab].steps.map((t, i) => (
              <li key={i}>
                {typeof t === "string" ? t : <>
                  {t.text}
                  <code className="block mt-1 rounded bg-white px-2 py-1.5 font-mono text-[11px] ring-1 ring-slate-200 break-all select-all">{t.code}</code>
                </>}
              </li>
            ))}
          </ol>
          <div className="flex flex-wrap items-center gap-3 pt-1">
            <button type="button" onClick={test} disabled={testing || !isChromium()} data-testid="silent-print-test"
                    className="inline-flex h-9 items-center gap-2 rounded-lg bg-slate-900 px-3 text-xs font-semibold text-white disabled:opacity-60">
              {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Printer className="h-3.5 w-3.5" />} Test on this device
            </button>
            <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
              <MonitorCheck className="h-3.5 w-3.5" />
              {isChromium() ? "Prints a sample badge and detects whether the dialog appeared." : "Detection works in Chrome or Edge only."}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
